import { useMemo, useState } from 'react'
import { useAuth } from '../config/context/auth'
import { useTranslation } from 'react-i18next'
import { useAsync } from 'react-use'
import { getTeamAccessSummary } from '../api/team'
import capitalize from 'lodash/capitalize'
import Section from './Section'
import { DataTable } from '@/components/ui/data-table'
import { ColumnDef } from '@tanstack/react-table'
import { usePageInfo } from '../config/queries'
import LoginButtons from './LoginButtons'
import TroubleshootingModal from '../pages/UserDashboard/components/TroubleshootingModal'
import { Button, buttonVariants } from '@/components/ui/button'
import { refreshPersonalCharacters } from '../api/user'
import dayjs from 'dayjs'
import { updateGuild } from '../api/guild'
import { useGuild } from '../config/context/guild'
import Card from './Card'
import LinkButton from '@/components/ui/link-button'
import { useRefreshData } from '../hooks/useRefreshData'

const UnauthorizedInfo = ({
  teamReference,
}: {
  teamReference?: wowaudit.TeamReference
}) => {
  const { t } = useTranslation()
  const {
    data: { user },
  } = usePageInfo()
  const { guildReference } = useGuild()
  const { accessLevel, section, requirement } = useAuth()
  const [helpModalOpen, setHelpModalOpen] = useState(false)
  const { value: accessDetails } = useAsync(async () => {
    return teamReference
      ? await getTeamAccessSummary(teamReference.id)
      : undefined
  }, [teamReference])

  const tokenExpired = user && dayjs(user.oauthExpiresAt).isBefore(dayjs())
  const { refreshing, refreshData } = useRefreshData({
    retriever: refreshPersonalCharacters,
    queryToInvalidate: ['pageInfo'],
    callback: () => window.location.reload(),
  })

  const { refreshing: refreshingGuild, refreshData: refreshGuild } =
    useRefreshData({
      retriever: updateGuild,
      id: guildReference?.id,
      callback: () => refreshData(),
    })

  const relevantRestrictions = useMemo(() => {
    if (!accessDetails) return undefined

    const sectionSpecific = accessDetails.accessRestrictions.filter(
      (r) =>
        r.restrictionType !== 'user' &&
        r.section === section &&
        (requirement === r.accessLevel || r.accessLevel === 'admin'),
    )
    if (sectionSpecific.length > 0) {
      return sectionSpecific
    }

    return accessDetails.accessRestrictions.filter(
      (r) =>
        r.restrictionType !== 'user' &&
        r.section === 'base' &&
        (requirement === r.accessLevel || r.accessLevel === 'admin'),
    )
  }, [accessDetails, section, requirement])

  const columns: ColumnDef<wowaudit.AccessRestriction>[] = useMemo(
    () => [
      {
        accessorKey: 'restrictionType',
        header: 'Access by',
        cell: (info) => {
          const restrictionType = info.getValue() as string
          return capitalize(restrictionType.replace('_', ' '))
        },
      },
      {
        id: 'identifier',
        header: 'Required rank',
        cell: (info) => {
          if (info.row.original.restrictionType === 'game_rank') {
            return info.row.original.gameRankThreshold
          } else if (info.row.original.restrictionType === 'team_rank') {
            return (
              accessDetails?.teamRanks.find(
                (r) => r.id === info.row.original.teamRankId,
              )?.name ?? ''
            )
          }
          return '-'
        },
      },
      {
        accessorKey: 'accessLevel',
        header: 'Level',
        cell: (info) => {
          return capitalize(info.getValue() as string)
        },
      },
      {
        id: 'userStatus',
        header: 'Your rank',
        cell: (info) => {
          if (info.row.original.restrictionType === 'game_rank') {
            return (
              <span className='text-destructive-foreground'>
                {accessDetails?.bestRank ?? 'not in guild'}
              </span>
            )
          } else if (info.row.original.restrictionType === 'team_rank') {
            return (
              <span className='text-destructive-foreground'>
                {(accessDetails?.teamRanksForCurrentUser ?? [])
                  .map(
                    (rank) =>
                      accessDetails?.teamRanks.find((r) => r.id === rank.id)
                        ?.name ?? '',
                  )
                  .join(', ') || 'not in team'}
              </span>
            )
          }
          return '-'
        },
      },
    ],
    [accessDetails],
  )

  return (
    <Section title={t('errors.unauthorized')} className='text-center mt-16'>
      <h3>{t('errors.unauthorized')}</h3>
      <p>
        <strong>{capitalize(requirement)}</strong> level
        {section === 'base' ? ' base' : ' '} access
        {section === 'base' ? ' ' : ` to the ${section} section`} is required to
        view this team page, but you currently have{' '}
        <strong>{accessLevel === 'none' ? 'no' : accessLevel}</strong> access.
        {user
          ? user.characterReferences.length > 0
            ? ' Meeting one of the following rules would allow you to access this section:'
            : ''
          : ' Have you tried logging in?'}
      </p>

      {!user ? (
        <Card className='my-6 py-3 px-4 inline-flex'>
          <LoginButtons />
        </Card>
      ) : null}

      {user && user.characterReferences.length === 0 ? (
        <Card className='my-6 p-5 inline-flex'>
          <div>
            <p>{t('errors.no_user_characters_found')}</p>

            <div className='flex gap-2 justify-center mt-3'>
              {guildReference?.path ? (
                <LinkButton to={guildReference?.path}>View guild</LinkButton>
              ) : null}

              {tokenExpired ? (
                <a
                  href={`/auth/bnet`}
                  className={`${buttonVariants({ variant: 'outline' })}`}
                >
                  Re-authenticate
                </a>
              ) : (
                <Button
                  variant='outline'
                  onClick={() => refreshData()}
                  loading={refreshing}
                  spinner='blizzard'
                >
                  Fetch characters
                </Button>
              )}

              <Button variant='outline' onClick={() => setHelpModalOpen(true)}>
                Help
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {helpModalOpen && (
        <TroubleshootingModal
          inProfile={false}
          setOpen={(value: boolean) => setHelpModalOpen(value)}
        />
      )}

      {user &&
      user.characterReferences.length > 0 &&
      relevantRestrictions?.length ? (
        <div className='flex flex-col gap-6 items-center justify-center mt-6 text-left'>
          <DataTable
            columns={columns}
            data={relevantRestrictions}
            includeHeader={true}
          />

          <div className='flex gap-2 justify-center'>
            {guildReference?.path ? (
              <LinkButton to={guildReference?.path}>View guild</LinkButton>
            ) : null}

            {tokenExpired ? (
              <a
                className={`${buttonVariants({ variant: 'outline' })}`}
                href={`/auth/bnet`}
              >
                Re-authenticate
              </a>
            ) : (
              <Button
                variant='outline'
                onClick={() => refreshGuild()}
                loading={refreshing || refreshingGuild}
                spinner='blizzard'
              >
                Sync access
              </Button>
            )}

            <Button variant='outline' onClick={() => setHelpModalOpen(true)}>
              Help
            </Button>
          </div>
        </div>
      ) : null}
    </Section>
  )
}

export default UnauthorizedInfo
